/*
  Éditions Sésames : minimum de commande (octobre 2026).
  - Règle : dès qu'une ligne est à expédier, le total du panier (remises déduites, hors cartes-cadeaux) doit atteindre
    le minimum ; un panier uniquement numérique n'en a pas.
  - Sous le minimum, les boutons « Procéder au paiement » du panier, de la fenêtre d'ajout et du tiroir restent
    visibles mais inactifs (aria-disabled) : un clic ou la touche Entrée montre le message qui dit ce qu'il manque.
    Les paiements express du panier sont masqués par la classe « s-minimum-bloque » de <html>.
  - Réglages et état au chargement : <script id="sesames-minimum-commande"> (snippets/sesames-minimum-commande.liquid).
  - Après chaque ajout, modification ou suppression dans le panier (fetch ou XHR vers /cart/add, /cart/change,
    /cart/update, /cart/clear), le total est relu sur /cart.js.
  Pour vérifier : window.SesamesMinimumCommande.etat().
*/
(() => {
  const source = document.getElementById('sesames-minimum-commande');
  if (!source || window.SesamesMinimumCommande) return;
  let reglages;
  try {
    reglages = JSON.parse(source.textContent);
  } catch (erreur) {
    return;
  }
  const MINIMUM = Number(reglages.minimum) || 0;
  if (MINIMUM <= 0) return;

  const BOUTONS = 'button[name="checkout"], input[name="checkout"], a[href$="/checkout"], a[href*="/checkout?"]';
  const MODIFICATION = /\/cart\/(add|change|update|clear)(\.js(on)?)?\/?(\?|#|$)/;
  const format = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: reglages.devise || 'EUR' });
  const montant = (centimes) => format.format(centimes / 100);
  const fetchOriginal = window.fetch.bind(window);

  let aExpedier = reglages.aExpedier === true;
  let total = Number(reglages.total) || 0;
  let numero = 0;
  const bloque = () => aExpedier && total < MINIMUM;

  /* Le message se place juste avant le groupe de boutons (panier, tiroir) ou le formulaire (fenêtre d'ajout). */
  const lieu = (bouton) => bouton.closest('.cart__ctas') || bouton.closest('form') || bouton;
  const messageDe = (bouton) => {
    const voisin = lieu(bouton).previousElementSibling;
    return voisin && voisin.matches('[data-s-minimum]') ? voisin : null;
  };
  const creerMessage = (bouton) => {
    const message = document.createElement('p');
    message.className = 's-minimum';
    message.id = `SMinimum-${(numero += 1)}`;
    message.setAttribute('data-s-minimum', '');
    message.setAttribute('role', 'status');
    message.tabIndex = -1;
    message.append(document.createElement('strong'), document.createElement('span'));
    lieu(bouton).before(message);
    return message;
  };
  /* N'écrire que ce qui change : l'observateur du DOM rappelle appliquer() après chaque écriture. */
  const ecrire = (element, texte) => {
    if (element.textContent !== texte) element.textContent = texte;
  };
  const decrire = (bouton, id, ajouter) => {
    const avant = bouton.getAttribute('aria-describedby') || '';
    const ids = avant.split(/\s+/).filter((x) => x && !x.startsWith('SMinimum-'));
    if (ajouter) ids.push(id);
    const apres = ids.join(' ');
    if (apres === avant) return;
    if (apres) bouton.setAttribute('aria-describedby', apres);
    else bouton.removeAttribute('aria-describedby');
  };

  const appliquer = () => {
    const actif = bloque();
    document.documentElement.classList.toggle('s-minimum-bloque', actif);
    document.querySelectorAll(BOUTONS).forEach((bouton) => {
      let message = messageDe(bouton);
      if (actif) {
        if (!message) message = creerMessage(bouton);
        ecrire(message.firstChild, `Il vous manque ${montant(MINIMUM - total)} pour commander`);
        ecrire(
          message.lastChild,
          `Le minimum de commande est de ${montant(MINIMUM)} ${reglages.mention}, sauf pour une commande uniquement numérique.`
        );
        if (bouton.getAttribute('aria-disabled') !== 'true') bouton.setAttribute('aria-disabled', 'true');
        bouton.classList.add('s-minimum-inactif');
        decrire(bouton, message.id, true);
      } else {
        if (message) {
          decrire(bouton, message.id, false);
          message.remove();
        }
        if (bouton.classList.contains('s-minimum-inactif')) {
          bouton.classList.remove('s-minimum-inactif');
          bouton.removeAttribute('aria-disabled');
        }
      }
    });
  };

  const signaler = (bouton) => {
    appliquer();
    const message = bouton && messageDe(bouton);
    if (!message) return;
    message.classList.remove('s-minimum--attention');
    void message.offsetWidth; /* relance l'animation */
    message.classList.add('s-minimum--attention');
    message.focus({ preventScroll: true });
    const doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    message.scrollIntoView({ block: 'nearest', behavior: doux ? 'smooth' : 'auto' });
  };

  /* Sous le minimum : ni clic ni envoi de formulaire vers le paiement. */
  document.addEventListener(
    'click',
    (evenement) => {
      if (!bloque() || !evenement.target.closest) return;
      const bouton = evenement.target.closest(BOUTONS);
      if (!bouton) return;
      evenement.preventDefault();
      evenement.stopImmediatePropagation();
      signaler(bouton);
    },
    true
  );
  document.addEventListener(
    'submit',
    (evenement) => {
      if (!bloque()) return;
      const declencheur = evenement.submitter;
      const action = evenement.target.getAttribute('action') || '';
      if (!(declencheur && declencheur.matches(BOUTONS)) && !/\/checkout/.test(action)) return;
      evenement.preventDefault();
      evenement.stopImmediatePropagation();
      signaler(declencheur);
    },
    true
  );

  /* Relecture du panier : la dernière demande partie l'emporte. */
  let minuterie = null;
  let version = 0;
  const relire = () => {
    clearTimeout(minuterie);
    minuterie = setTimeout(async () => {
      const demande = (version += 1);
      try {
        const racine = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const reponse = await fetchOriginal(`${racine}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!reponse.ok) return;
        const panier = await reponse.json();
        if (demande !== version) return;
        const lignes = panier.items || [];
        aExpedier = lignes.some((ligne) => ligne.requires_shipping);
        total = lignes.reduce(
          (somme, ligne) => somme - (ligne.gift_card ? Number(ligne.final_line_price) || 0 : 0),
          Number(panier.total_price) || 0
        );
        appliquer();
      } catch (erreur) {
        /* réseau : on garde l'état connu */
      }
    }, 120);
  };

  window.fetch = function (ressource, options) {
    const promesse = fetchOriginal(ressource, options);
    try {
      const adresse = typeof Request !== 'undefined' && ressource instanceof Request ? ressource.url : String(ressource);
      if (MODIFICATION.test(adresse)) promesse.then(relire, relire);
    } catch (erreur) {
      /* adresse illisible : rien à relire */
    }
    return promesse;
  };
  const ouvrirXhr = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (methode, adresse) {
    try {
      if (MODIFICATION.test(String(adresse))) this.addEventListener('loadend', relire);
    } catch (erreur) {
      /* adresse illisible : rien à relire */
    }
    return ouvrirXhr.apply(this, arguments);
  };
  window.addEventListener('pageshow', (evenement) => {
    if (evenement.persisted) relire();
  });

  /* Les sections du panier sont redessinées après chaque modification : on repose l'état sur les nouveaux boutons. */
  let prevu = false;
  const observateur = new MutationObserver(() => {
    if (prevu) return;
    prevu = true;
    requestAnimationFrame(() => {
      prevu = false;
      appliquer();
    });
  });
  const demarrer = () => {
    appliquer();
    observateur.observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();

  window.SesamesMinimumCommande = {
    etat: () => ({ minimum: MINIMUM, mention: reglages.mention, aExpedier, total, bloque: bloque() }),
    relire,
  };
})();
